import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  Timestamp,
  where,
  WhereFilterOp,
  DocumentSnapshot as LiteDocumentSnapshot,
  QueryDocumentSnapshot,
} from "firebase/firestore/lite";
import { DocumentSnapshot } from "@firebase/firestore-types";
import { envVars } from "@ambii-com/core";
import { fsLocations } from "~/dao/FsLocations";
import { normalizeTimestampsInData } from "~/utils/fsJs";

let liteDb: ReturnType<typeof getFirestore> | null = null;

function getLiteDb() {
  if (!liteDb) {
    liteDb = getFirestore();
  }
  return liteDb;
}

function chunkArray<T>(list: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < list.length; i += size) {
    chunks.push(list.slice(i, i + size));
  }
  return chunks;
}

function normalizeLiteDocData(
  data: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!data) {
    return data;
  }
  return normalizeTimestampsInData(data);
}

/** Map Firestore Lite snapshot → compat shape for @ambii-com/core fromDoc */
function toCompatDocSnapshot(
  snap: LiteDocumentSnapshot | QueryDocumentSnapshot,
  collectionName: string
): DocumentSnapshot {
  const raw = snap.data() as Record<string, unknown> | undefined;
  const data = normalizeLiteDocData(raw);
  return {
    id: snap.id,
    exists: snap.exists(),
    data: () => data,
    ref: fsLocations.db.collection(collectionName).doc(snap.id),
  } as DocumentSnapshot;
}

export async function liteGetDocument(
  collectionName: string,
  documentIdValue: string
): Promise<DocumentSnapshot> {
  const snap = await getDoc(doc(getLiteDb(), collectionName, documentIdValue));
  return toCompatDocSnapshot(snap, collectionName);
}

type WhereClause = [field: string, op: WhereFilterOp, value: unknown];
type OrderByDirection = "asc" | "desc";

export async function liteQueryCollection(
  collectionName: string,
  field: string,
  value: string
): Promise<DocumentSnapshot[]> {
  return liteQueryCollectionWith(collectionName, [field, "==", value]);
}

export async function liteQueryCollectionWith(
  collectionName: string,
  ...wheres: WhereClause[]
): Promise<DocumentSnapshot[]> {
  const constraints = wheres.map(([field, op, value]) => where(field, op, value));
  const q = query(collection(getLiteDb(), collectionName), ...constraints);
  const result = await getDocs(q);
  return result.docs.map((docSnap) => toCompatDocSnapshot(docSnap, collectionName));
}

export async function liteQueryCollectionLimited(
  collectionName: string,
  max: number,
  ...wheres: WhereClause[]
): Promise<DocumentSnapshot[]> {
  const constraints = wheres.map(([field, op, value]) => where(field, op, value));
  const q = query(collection(getLiteDb(), collectionName), ...constraints, limit(max));
  const result = await getDocs(q);
  return result.docs.map((docSnap) => toCompatDocSnapshot(docSnap, collectionName));
}

export async function liteQueryCollectionOrderedLimited(
  collectionName: string,
  orderField: string,
  direction: OrderByDirection,
  max: number,
  ...wheres: WhereClause[]
): Promise<DocumentSnapshot[]> {
  const constraints = wheres.map(([field, op, value]) => where(field, op, value));
  const q = query(
    collection(getLiteDb(), collectionName),
    ...constraints,
    orderBy(orderField, direction),
    limit(max)
  );
  const result = await getDocs(q);
  return result.docs.map((docSnap) => toCompatDocSnapshot(docSnap, collectionName));
}

export async function liteGetDocumentsIn(
  collectionName: string,
  ids: string[]
): Promise<DocumentSnapshot[]> {
  if (ids.length === 0) {
    return [];
  }
  const chunks = chunkArray(ids, 10);
  const results = await Promise.all(
    chunks.map(async (chunk) => {
      const q = query(
        collection(getLiteDb(), collectionName),
        where(documentId(), "in", chunk)
      );
      const snap = await getDocs(q);
      return snap.docs.map((docSnap) =>
        toCompatDocSnapshot(docSnap, collectionName)
      );
    })
  );
  const byId = new Map(
    results.flat().map((docSnap) => [docSnap.id, docSnap] as const)
  );
  return ids
    .map((id) => byId.get(id))
    .filter((docSnap): docSnap is DocumentSnapshot => docSnap !== undefined);
}

export const liteCollections = {
  clinics: envVars.clinicCollectionName,
  forms: envVars.formsCollectionName,
  formConfigs: envVars.formsConfigCollectionName,
  formPages: envVars.pagesCollectionName,
  formQuestions: envVars.questionsCollectionName,
  formSubmissions: envVars.submissionsCollectionName,
  appointmentMenus: envVars.appointmentMenusCollectionName,
  appointmentConfigs: envVars.appointmentConfigsCollectionName,
  appointmentSchedules: envVars.appointmentSchedulesCollectionName,
  scheduleAppointments: envVars.scheduleAppointmentCollectionName,
  patients: envVars.patientCollectionName,
  admins: envVars.adminCollectionName,
  superadmins: envVars.superadminCollectionName,
  vaccineConfigs: envVars.vaccineConfigCollectionName,
  posts: envVars.postsCollectionName,
  scoringGroups: envVars.scoringGroupsCollectionName,
  pdfExportForms: "pdfExportForms",
} as const;

export { Timestamp };
